/**
 * SectionActionButton — reusable hover action button for CollapsibleSection headers.
 *
 * Renders a brand-colored text + icon button that appears on hover.
 * CollapsibleSection already handles the hover visibility transition,
 * so this component only provides the styling and layout.
 */

import { Tooltip } from '@/lib/base/tooltip/tooltip';

interface SectionActionButtonProps {
  /** Button label text */
  label: string;
  /** Click handler */
  onClick: (() => void) | (() => Promise<unknown>);
  /** Optional icon component rendered before the label */
  icon?: React.ComponentType<{ className?: string }>;
  /** Disable the button (e.g. while loading) */
  disabled?: boolean;
  /**
   * Tooltip explaining why the button is disabled (e.g. "Loading…"). Shown only
   * while `disabled` is true; ignored otherwise. When present, the disabled
   * button is driven by `aria-disabled` rather than the native `disabled`
   * attribute so it stays hoverable/focusable and can surface the tooltip
   * (native-disabled controls do not fire pointer/focus events).
   */
  disabledTooltip?: string;
}

export function SectionActionButton({
  label,
  onClick,
  icon: Icon,
  disabled,
  disabledTooltip,
}: SectionActionButtonProps) {
  const gated = Boolean(disabled && disabledTooltip);
  const button = (
    <button
      type='button'
      onClick={gated ? undefined : onClick}
      disabled={disabled && !disabledTooltip}
      aria-disabled={disabled || undefined}
      className='flex items-center gap-1 text-xs md:text-sm text-fg-brand-primary hover:text-utility-brand-700 cursor-pointer bg-transparent hover:bg-[var(--color-bg-tertiary)] border-none px-2 py-1 -my-1 rounded-md disabled:opacity-50 disabled:cursor-default aria-disabled:opacity-50 aria-disabled:cursor-default transition-colors'
    >
      {Icon && <Icon className='w-3.5 h-3.5' />}
      {label}
    </button>
  );

  if (gated) {
    return (
      <Tooltip title={disabledTooltip} placement='top' interactive>
        {button}
      </Tooltip>
    );
  }
  return button;
}
